import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ListBulletIcon, UsersIcon, UserCircleIcon, EllipsisVerticalIcon, ArchiveBoxIcon, ArrowUturnLeftIcon, TrashIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useApi } from '../Api';
import { dagToast } from '../utils/glowUp';
import { formatRelativeTime } from '../utils/dateFormatting';
import { ConversationListProps, ConversationStatus, ConversationCategory, Family } from '../types/Messaging';

const HEADER_HEIGHT = "min-h-[56px]";
const statusOptions = [
  { tab: 'new', label: 'New' },
  { tab: 'open', label: 'Open' },
  { tab: 'closed', label: 'Closed' },
  { tab: 'archived', label: 'Archived' }
];

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  families,
  onSelectConversation,
  onSelectFamily,
  onConversationArchived,
  onConversationDeleted,
  showDate = false,
  selectedConversation,
  onNewConversation,
}) => {
  const [activeTab, setActiveTab] = useState<'new' | 'open' | 'closed' | 'archived'>('new');
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [familyDropdownOpen, setFamilyDropdownOpen] = useState(false);
  const [statusButtonRect, setStatusButtonRect] = useState<DOMRect | null>(null);
  const [familyButtonRect, setFamilyButtonRect] = useState<DOMRect | null>(null);
  const [contextMenuConversationId, setContextMenuConversationId] = useState<number | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const familyButtonRef = useRef<HTMLButtonElement>(null);
  const api = useApi();

  // Detect mobile
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only include families that are referenced by a conversation's familyId
  const familiesWithConversations = families.filter(f =>
    conversations.some(c => c.familyId === f.id)
  );

  const handleFamilySelect = (family: Family | null) => {
    setSelectedFamily(family);
    if (onSelectFamily) onSelectFamily(family);
  };

  const handleArchiveConversation = async (conversationId: number) => {
    try {
      setErrorMessage(null);
      const response = await api.put(`/api/messaging/UpdateConversationStatus/${conversationId}`, 3);
      
      if (response.data?.success) {
        if (onConversationArchived) {
          onConversationArchived();
        }
        closeContextMenu();
        dagToast('Conversation archived', 'success');
      } else {
        setErrorMessage(response.data?.message || 'Failed to archive conversation');
      }
    } catch (error: any) {
      console.error('Error archiving conversation:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to archive conversation');
    }
  };

  const handleDeleteConversation = async (conversationId: number) => {
    try {
      setErrorMessage(null);
      const response = await api.delete(`/api/messaging/DeleteConversation/${conversationId}`);
      
      if (response.data?.success) {
        if (onConversationDeleted) {
          onConversationDeleted();
        }
        closeContextMenu();
        dagToast('Conversation deleted', 'success');
      } else {
        setErrorMessage(response.data?.message || 'Failed to delete conversation');
      }
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to delete conversation');
    }
  };

  const handleUnarchiveConversation = async (conversationId: number) => {
    try {
      setErrorMessage(null);
      const response = await api.put(`/api/messaging/UpdateConversationStatus/${conversationId}`, 1); // Status.OPEN
      
      if (response.data?.success) {
        if (onConversationArchived) {
          onConversationArchived();
        }
        closeContextMenu();
        dagToast('Conversation restored', 'success');
      } else {
        setErrorMessage(response.data?.message || 'Failed to unarchive conversation');
      }
    } catch (error: any) {
      console.error('Error unarchiving conversation:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to unarchive conversation');
    }
  };

  const handleContextMenu = (e: React.MouseEvent, conversationId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuConversationId(conversationId);
    
    // Calculate position to avoid going off-screen
    const menuWidth = 120; // Approximate width of the context menu
    const windowWidth = window.innerWidth;
    const mouseX = e.clientX;
    
    // If the menu would go off-screen to the right, position it to the left of the mouse
    const x = (mouseX + menuWidth > windowWidth) ? mouseX - menuWidth : mouseX;
    
    setContextMenuPosition({ x, y: e.clientY });
  };

  const closeContextMenu = () => {
    setContextMenuConversationId(null);
    setContextMenuPosition(null);
  };

  // Close context menu and dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      closeContextMenu();
      
      // Close dropdowns when clicking outside
      const target = event.target as Element;
      if (statusDropdownOpen && !target.closest('[data-dropdown="status"]') && !target.closest('.portal-dropdown')) {
        setStatusDropdownOpen(false);
      }
      if (familyDropdownOpen && !target.closest('[data-dropdown="family"]') && !target.closest('.portal-dropdown')) {
        setFamilyDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [statusDropdownOpen, familyDropdownOpen]);

  const filteredConversations = conversations.filter(c => {
    const statusMatch =
      (activeTab === 'new' && c.status === ConversationStatus.NEW) ||
      (activeTab === 'open' && c.status === ConversationStatus.OPEN) ||
      (activeTab === 'closed' && c.status === ConversationStatus.CLOSED) ||
      (activeTab === 'archived' && c.status === ConversationStatus.ARCHIVED);

    if (!statusMatch) return false;
    if (selectedFamily) {
      return c.familyId === selectedFamily.id;
    }
    return true;
  });

  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const statusOrder = [
      ConversationStatus.NEW,
      ConversationStatus.OPEN,
      ConversationStatus.CLOSED,
      ConversationStatus.ARCHIVED,
    ];
    const aIndex = statusOrder.indexOf(a.status);
    const bIndex = statusOrder.indexOf(b.status);
    if (aIndex !== bIndex) return aIndex - bIndex;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const formatFullDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCategory = (category: ConversationCategory) => {
    switch (category) {
      case ConversationCategory.GENERAL:
        return 'General';
      case ConversationCategory.ADDITIONAL_DOCUMENTS:
        return 'Additional Documents';
      default:
        return 'Unknown';
    }
  };

  // Finds the current status label for dropdown
  const currentStatusLabel = statusOptions.find(opt => opt.tab === activeTab)?.label ?? activeTab;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 lg:gap-6 w-full items-start">
      {/* Status and Families Stacked */}
      <div className="lg:col-span-3 space-y-4">
        {/* Status Card */}
        <div className="rounded-xl shadow-md overflow-hidden">
          <div className="bg-white flex flex-col" style={{ clipPath: 'inset(0 round 0.75rem)' }}>
            <div className={`flex items-center px-6 border-b border-gray-200 bg-white font-semibold text-gray-900 text-lg ${HEADER_HEIGHT}`}>
              <ListBulletIcon className="w-5 h-5 text-gray-400 mr-2" />
              Status
            </div>
        {isMobile ? (
          <div className="px-4 pb-4">
            <div className="relative mt-4">
              <button
                ref={statusButtonRef}
                data-dropdown="status"
                type="button"
                className="w-full flex items-center justify-between px-4 py-2 rounded-md bg-gradient-to-r from-green-600 to-emerald-700 text-white text-base font-medium shadow-none focus:outline-none"
                onClick={() => {
                  if (statusButtonRef.current) {
                    setStatusButtonRect(statusButtonRef.current.getBoundingClientRect());
                  }
                  setStatusDropdownOpen(open => !open);
                }}
              >
                <span>{currentStatusLabel}</span>
                <ChevronDownIcon
                  className={`w-5 h-5 ml-2 transition-transform ${statusDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

            </div>
          </div>
        ) : (
          <div className="px-4 mb-2">
            <div className="space-y-2 mt-4">
              {statusOptions.map(opt => {
                const isActive = activeTab === opt.tab;
                return (
                  <button
                    key={opt.tab}
                    type="button"
                    onClick={() => setActiveTab(opt.tab as typeof activeTab)}
                    className={`w-full text-left pl-4 pr-2 py-2 rounded-md text-base transition-colors ${isActive
                      ? 'bg-gradient-to-r from-green-600 to-emerald-700 text-white font-medium'
                      : 'bg-white text-gray-700 hover:bg-gray-100 focus:outline-none'
                      }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
          </div>
        </div>

        {/* Families Card */}
        <div className="rounded-xl shadow-md overflow-hidden">
          <div className="bg-white flex flex-col" style={{ clipPath: 'inset(0 round 0.75rem)' }}>
            {/* Families header */}
            <div className={`flex items-center px-6 border-b border-gray-200 bg-white font-semibold text-gray-900 text-lg ${HEADER_HEIGHT}`}>
              <UsersIcon className="w-5 h-5 text-gray-400 mr-2" />
              Families
            </div>
        {isMobile ? (
          <div className="px-4 pb-4">
            <div className="relative mt-4">
              <button
                ref={familyButtonRef}
                data-dropdown="family"
                type="button"
                className="w-full flex items-center justify-between px-4 py-2 rounded-md bg-gradient-to-r from-gray-500 to-gray-600 text-white text-base font-medium shadow-none focus:outline-none"
                onClick={() => {
                  if (familyButtonRef.current) {
                    setFamilyButtonRect(familyButtonRef.current.getBoundingClientRect());
                  }
                  setFamilyDropdownOpen(open => !open);
                }}
              >
                <span>{selectedFamily ? selectedFamily.name : "All Families"}</span>
                <ChevronDownIcon
                  className={`w-5 h-5 ml-2 transition-transform ${familyDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

            </div>
          </div>
        ) : (
          <div className="px-4 pb-5">
            <div className="space-y-2 mt-4">
              {familiesWithConversations.map(family => (
                <button
                  key={family.id}
                  onClick={() =>
                    selectedFamily?.id === family.id
                      ? handleFamilySelect(null)
                      : handleFamilySelect(family)
                  }
                  className={`w-full text-left pl-4 pr-2 py-2 rounded-md text-base transition-colors ${selectedFamily?.id === family.id
                    ? 'text-white font-medium bg-gradient-to-r from-green-600 to-emerald-700'
                    : 'hover:bg-gray-100 text-gray-700'
                    }`}
                >
                  {family.name}
                </button>
              ))}
              {familiesWithConversations.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">No families with conversations</p>
              )}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Conversation List Card */}
      <div className="lg:col-span-3 rounded-xl shadow-md overflow-hidden">
        <div className="bg-white flex flex-col h-full" style={{ clipPath: 'inset(0 round 0.75rem)' }}>
          {/* Conversations header */}
          <div className={`flex items-center justify-between px-6 border-b border-gray-200 bg-white font-semibold text-gray-900 text-lg ${HEADER_HEIGHT}`}>
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400 mr-2" />
              Conversations
            </div>
            {onNewConversation && (
              <button
                onClick={onNewConversation}
                className="flex items-center gap-1 text-white font-semibold px-3 py-1.5 rounded-md text-sm transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(to right, var(--green-600), var(--emerald-700))',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, var(--green-700), var(--emerald-700))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, var(--green-600), var(--emerald-700))';
                }}
              >
                <PlusIcon className="w-4 h-4" />
                <span>New</span>
              </button>
            )}
          </div>
        <div className={`overflow-y-auto ${isMobile ? '' : ''}`} style={{ height: isMobile ? 'auto' : 'calc(100vh - 360px)' }}>
          {sortedConversations.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              {selectedFamily
                ? `No ${activeTab} conversations for ${selectedFamily.name} at this time.`
                : `No ${activeTab} conversations at this time.`}
            </div>
          ) : (
            sortedConversations.map(conversation => {
            const displayDate = conversation.lastMessageDate || conversation.updatedAt;
            const isSelected =
              selectedConversation && selectedConversation.id === conversation.id;
            // Get family name by lookup
            const familyName = families.find(fam => fam.id === conversation.familyId)?.name ?? "Unknown Family";
            return (
              <div
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                onContextMenu={(e) => handleContextMenu(e, conversation.id)}
                className={`flex items-center p-4 border-b border-gray-100 cursor-pointer transition-colors relative
                  ${isSelected
                    ? 'dag-conv-selected'
                    : 'hover:bg-gray-50'
                  }
                `}
              >
                <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <UserCircleIcon className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0 ml-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {conversation.otherParticipantName || `Client ${conversation.clientId}`}
                        </h3>
                        {conversation.status === ConversationStatus.ARCHIVED && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Archived
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{formatCategory(conversation.category)}</p>
                      <p className="text-xs text-gray-400 truncate">Family: {familyName}</p>
                      {conversation.lastMessageContent && (
                        <p className="text-sm text-gray-600 truncate mt-1">{conversation.lastMessageContent}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {showDate && (
                        <div className="ml-4 flex-shrink-0" title={formatFullDate(displayDate)}>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {formatRelativeTime(displayDate)}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContextMenu(e, conversation.id);
                        }}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
          )}
        </div>
        </div>
      </div>


      {/* Context Menu */}
      {contextMenuPosition && (
        <div
          className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-50 py-1 min-w-[120px]"
          style={{
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
          }}
        >
          {(() => {
            const conversation = conversations.find(c => c.id === contextMenuConversationId);
            if (!conversation) return null;

            if (conversation.status === ConversationStatus.ARCHIVED) {
              // Show unarchive and delete options for archived conversations
              return (
                <>
                  <button
                    onClick={() => contextMenuConversationId && handleUnarchiveConversation(contextMenuConversationId)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <ArrowUturnLeftIcon className="w-4 h-4" />
                    Unarchive
                  </button>
                  <button
                    onClick={() => contextMenuConversationId && handleDeleteConversation(contextMenuConversationId)}
                    className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </>
              );
            } else {
              // Show archive and delete options for non-archived conversations
              return (
                <>
                  <button
                    onClick={() => contextMenuConversationId && handleArchiveConversation(contextMenuConversationId)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <ArchiveBoxIcon className="w-4 h-4" />
                    Archive
                  </button>
                  <button
                    onClick={() => contextMenuConversationId && handleDeleteConversation(contextMenuConversationId)}
                    className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </>
              );
            }
          })()}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md shadow-lg z-50">
          <div className="flex items-center justify-between">
            <span className="text-sm">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* Portal Dropdowns */}
      {statusDropdownOpen && statusButtonRect && createPortal(
        <div
          className="fixed bg-white rounded-md shadow-lg z-50 divide-y divide-gray-100 min-w-[200px] portal-dropdown"
          style={{
            top: statusButtonRect.bottom + window.scrollY + 8,
            left: statusButtonRect.left + window.scrollX,
            width: statusButtonRect.width
          }}
        >
          {statusOptions
            .filter(opt => opt.tab !== activeTab)
            .map(opt => (
              <button
                key={opt.tab}
                type="button"
                className="w-full text-left px-4 py-2 text-base transition-colors hover:bg-gray-100 focus:outline-none"
                onClick={() => {
                  setActiveTab(opt.tab as typeof activeTab);
                  setStatusDropdownOpen(false);
                }}
              >
                {opt.label}
              </button>
            ))}
        </div>,
        document.body
      )}
      
      {familyDropdownOpen && familyButtonRect && createPortal(
        <div
          className="fixed bg-white rounded-md shadow-lg z-50 divide-y divide-gray-100 min-w-[200px] portal-dropdown"
          style={{
            top: familyButtonRect.bottom + window.scrollY + 8,
            left: familyButtonRect.left + window.scrollX,
            width: familyButtonRect.width
          }}
        >
          <button
            type="button"
            className="w-full text-left px-4 py-2 text-base transition-colors hover:bg-gray-100 focus:outline-none"
            onClick={() => {
              handleFamilySelect(null);
              setFamilyDropdownOpen(false);
            }}
          >
            All Families
          </button>
          {familiesWithConversations.map(family => (
            <button
              key={family.id}
              type="button"
              className="w-full text-left px-4 py-2 text-base transition-colors hover:bg-gray-100 focus:outline-none"
              onClick={() => {
                handleFamilySelect(family);
                setFamilyDropdownOpen(false);
              }}
            >
              {family.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ConversationList;